// ProjectMap.tsx — Leaflet-backed map for the ProjectView background.
//
// Replaces the OSM embed iframe so we can:
//   1) Capture pan/zoom events and persist them to project.mapView (the iframe
//      was cross-origin, so we couldn't read the user's framing).
//   2) Offer a Map / Satellite toggle (OSM mapnik vs Esri World Imagery).
//
// No vendor account or API key is needed: OSM and Esri World Imagery are both
// public tile services with attribution-only terms.

import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { MapView } from '../types/panelvar';

interface Coords { lat: number; lng: number }

interface Props {
  /** Geocoded project location — used to plant the marker and as the default center. */
  coords: Coords;
  /** Persisted view (center/zoom/layer). When present, restores instead of recentering. */
  savedView: MapView | null | undefined;
  /** Called on moveend / zoomend / layer-switch with the new view to persist. */
  onViewChange: (view: MapView) => void;
}

const TILE_OSM = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_SAT = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ATTR_OSM = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const ATTR_SAT = 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community';

// Leaflet ships marker images via require() — Vite needs the URLs explicitly.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon   from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const projectIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl:       markerIcon,
  shadowUrl:     markerShadow,
  iconSize:      [25, 41],
  iconAnchor:    [12, 41],
  popupAnchor:   [1, -34],
  shadowSize:    [41, 41],
});

export default function ProjectMap({ coords, savedView, onViewChange }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef       = useRef<L.Map | null>(null);
  const osmLayerRef  = useRef<L.TileLayer | null>(null);
  const satLayerRef  = useRef<L.TileLayer | null>(null);
  const markerRef    = useRef<L.Marker | null>(null);
  // Latest savedView, mirrored into a ref so the moveend handler can compare
  // without re-binding on every prop change.
  const savedRef     = useRef<MapView | null | undefined>(savedView);
  savedRef.current   = savedView;
  const onChangeRef  = useRef(onViewChange);
  onChangeRef.current = onViewChange;

  // One-time map setup. Subsequent prop changes are handled in the effects below.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const initial: MapView = savedView ?? { lat: coords.lat, lng: coords.lng, zoom: 16, layer: 'map' };

    const map = L.map(containerRef.current, {
      center: [initial.lat, initial.lng],
      zoom:   initial.zoom,
      zoomControl: true,
      attributionControl: true,
    });

    const osm = L.tileLayer(TILE_OSM, { attribution: ATTR_OSM, maxZoom: 19 });
    const sat = L.tileLayer(TILE_SAT, { attribution: ATTR_SAT, maxZoom: 19 });
    (initial.layer === 'satellite' ? sat : osm).addTo(map);

    const marker = L.marker([coords.lat, coords.lng], { icon: projectIcon }).addTo(map);

    mapRef.current      = map;
    osmLayerRef.current = osm;
    satLayerRef.current = sat;
    markerRef.current   = marker;

    const fire = () => {
      const c = map.getCenter();
      const z = map.getZoom();
      const layer: 'map' | 'satellite' = map.hasLayer(sat) ? 'satellite' : 'map';
      onChangeRef.current({ lat: c.lat, lng: c.lng, zoom: z, layer });
    };
    map.on('moveend zoomend', fire);

    return () => {
      map.off('moveend zoomend', fire);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount once; later prop changes flow through the effects below.

  // If the address changes (new geocode) AND there's no saved view yet, recenter.
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    marker.setLatLng([coords.lat, coords.lng]);
    if (!savedRef.current) {
      map.setView([coords.lat, coords.lng], map.getZoom() || 16);
    }
  }, [coords.lat, coords.lng]);

  const activeLayer: 'map' | 'satellite' = savedView?.layer ?? 'map';

  function switchLayer(next: 'map' | 'satellite') {
    const map = mapRef.current;
    const osm = osmLayerRef.current;
    const sat = satLayerRef.current;
    if (!map || !osm || !sat) return;
    if (next === 'satellite') {
      if (map.hasLayer(osm)) map.removeLayer(osm);
      if (!map.hasLayer(sat)) sat.addTo(map);
    } else {
      if (map.hasLayer(sat)) map.removeLayer(sat);
      if (!map.hasLayer(osm)) osm.addTo(map);
    }
    const c = map.getCenter();
    onChangeRef.current({ lat: c.lat, lng: c.lng, zoom: map.getZoom(), layer: next });
  }

  return (
    <div className="pv-map-leaflet-wrap">
      <div ref={containerRef} className="pv-map-leaflet" />
      <div className="pv-map-layer-toggle" role="group" aria-label="Map layer">
        <button
          type="button"
          className={`pv-map-layer-btn${activeLayer === 'map' ? ' active' : ''}`}
          onClick={() => switchLayer('map')}
          aria-pressed={activeLayer === 'map'}
        >Map</button>
        <button
          type="button"
          className={`pv-map-layer-btn${activeLayer === 'satellite' ? ' active' : ''}`}
          onClick={() => switchLayer('satellite')}
          aria-pressed={activeLayer === 'satellite'}
        >Satellite</button>
      </div>
    </div>
  );
}
